import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Users, UserCheck, CalendarDays, DollarSign, BarChart3 } from "lucide-react";
import { API } from "../lib/api";
import { sortByName } from "../lib/sortByName";
import { DatePicker } from "../components/DatePicker";

const Dashboard = () => {
  const [diarias, setDiarias] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [prestadores, setPrestadores] = useState([]);
  const [custosOperacionais, setCustosOperacionais] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Estado para filtros de período
  const [filtrosPeriodo, setFiltrosPeriodo] = useState({
    tipo: 'este_mes', // este_mes, ultimos_30_dias, trimestre, ano, customizado
    dataInicio: '',
    dataFim: ''
  });

  // Função para obter datas baseado no tipo de filtro
  const obterPeriodo = (tipo) => {
    const hoje = new Date();
    let inicio, fim;
    
    switch(tipo) {
      case 'este_mes':
        inicio = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
        fim = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);
        break;
      case 'ultimos_30_dias':
        fim = new Date(hoje);
        inicio = new Date(hoje);
        inicio.setDate(inicio.getDate() - 30);
        break;
      case 'trimestre':
        const trimestre = Math.floor(hoje.getMonth() / 3);
        inicio = new Date(hoje.getFullYear(), trimestre * 3, 1);
        fim = new Date(hoje.getFullYear(), (trimestre + 1) * 3, 0);
        break;
      case 'ano':
        inicio = new Date(hoje.getFullYear(), 0, 1);
        fim = new Date(hoje.getFullYear(), 11, 31);
        break;
      case 'customizado':
        inicio = filtrosPeriodo.dataInicio ? new Date(filtrosPeriodo.dataInicio) : null;
        fim = filtrosPeriodo.dataFim ? new Date(filtrosPeriodo.dataFim) : null;
        break;
      default:
        return { inicio: null, fim: null };
    }
    
    return { inicio, fim };
  };

  // Filtrar diárias por período
  const getDiariasFiltradas = () => {
    const { inicio, fim } = obterPeriodo(filtrosPeriodo.tipo);
    
    if (!inicio && !fim) return diarias;
    
    return diarias.filter(diaria => {
      const dataDiaria = diaria.data; // Manter como string YYYY-MM-DD
      const dataInicioStr = inicio ? inicio.toISOString().split('T')[0] : null;
      const dataFimStr = fim ? fim.toISOString().split('T')[0] : null;
      
      // Comparar as datas como strings no formato YYYY-MM-DD
      if (dataInicioStr && dataDiaria < dataInicioStr) return false;
      if (dataFimStr && dataDiaria > dataFimStr) return false;
      return true;
    });
  };

  // Obter período anterior para comparação
  const getDiariasComparacaoMensal = () => {
    const agora = new Date();
    const mesPassado = new Date(agora.getFullYear(), agora.getMonth() - 1, 1);
    const fimMesPassado = new Date(agora.getFullYear(), agora.getMonth(), 0);
    
    return diarias.filter(diaria => {
      const dataDiaria = new Date(diaria.data);
      return dataDiaria >= mesPassado && dataDiaria <= fimMesPassado;
    });
  };

  const fetchData = async () => {
    try {
      const [diariasRes, clientesRes, prestadoresRes, custosRes] = await Promise.all([
        axios.get(`${API}/diarias`),
        axios.get(`${API}/clientes`),
        axios.get(`${API}/prestadores`),
        axios.get(`${API}/custos`)
      ]);
      
      setDiarias(diariasRes.data);
      setClientes(clientesRes.data);
      // Ordenar prestadores alfabeticamente por nome
      const prestadoresOrdenados = prestadoresRes.data.sort(sortByName);
      setPrestadores(prestadoresOrdenados);
      setCustosOperacionais(custosRes.data);
      
    } catch (error) {
      toast.error("Erro ao carregar dados do dashboard");
    } finally {
      setLoading(false);
    }
  };

  // Filtrar custos operacionais por período
  const getCustosOperacionaisFiltrados = () => {
    const { inicio, fim } = obterPeriodo(filtrosPeriodo.tipo);
    
    if (!inicio && !fim) return custosOperacionais;
    
    return custosOperacionais.filter(custo => {
      const dataCusto = custo.data; // String YYYY-MM-DD
      const dataInicioStr = inicio ? inicio.toISOString().split('T')[0] : null;
      const dataFimStr = fim ? fim.toISOString().split('T')[0] : null;
      
      if (dataInicioStr && dataCusto < dataInicioStr) return false;
      if (dataFimStr && dataCusto > dataFimStr) return false;
      return true;
    });
  };

  // Calcular estatísticas baseadas nas diárias filtradas (estado derivado via useMemo)
  const stats = useMemo(() => {
    const diariasFiltradas = getDiariasFiltradas();
    const diariasComparacao = getDiariasComparacaoMensal();
    const custosOpFiltrados = getCustosOperacionaisFiltrados();

    const totalDiarias = diariasFiltradas.length;
    const faturamento = diariasFiltradas.reduce((sum, d) => sum + d.valor_cobrar, 0);
    const custosMaoObra = diariasFiltradas.reduce((sum, d) => sum + d.valor_pagar, 0);
    const custosOp = custosOpFiltrados.reduce((sum, c) => sum + c.valor, 0);
    const custosTotais = custosMaoObra + custosOp;

    const diariasPendentes = diariasFiltradas.filter(d => !d.pago).length;
    const prestadoresUnicosAtivos = [...new Set(diariasFiltradas.map(d => d.prestador_id))].length;

    // Calcular crescimento mensal
    const faturamentoMesPassado = diariasComparacao.reduce((sum, d) => sum + d.valor_cobrar, 0);
    const crescimentoMensal = faturamentoMesPassado > 0
      ? ((faturamento - faturamentoMesPassado) / faturamentoMesPassado) * 100
      : 0;

    return {
      totalDiarias,
      faturamento,
      custos: custosTotais, // Mão de obra + Custos operacionais
      margem: faturamento - custosTotais, // Margem real de lucro
      valorMedioDiaria: totalDiarias > 0 ? faturamento / totalDiarias : 0,
      diariasPendentes,
      prestadoresAtivos: prestadoresUnicosAtivos,
      crescimentoMensal
    };
  }, [diarias, custosOperacionais, filtrosPeriodo]);

  useEffect(() => {
    const carregar = async () => { await fetchData(); };
    carregar();
  }, []);

  const getClienteNome = (clienteId) => {
    const cliente = clientes.find(c => c.id === clienteId);
    return cliente ? cliente.nome : 'Cliente não encontrado';
  };

  const getPrestadorNome = (prestadorId) => {
    const prestador = prestadores.find(p => p.id === prestadorId);
    return prestador ? prestador.nome : 'Prestador não encontrado';
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatPercentage = (value) => {
    const signal = value > 0 ? '+' : '';
    return `${signal}${value.toFixed(1)}%`;
  };

  // Calcular diárias por cliente (filtradas)
  const getDiariasPorCliente = () => {
    const diariasFiltradas = getDiariasFiltradas();
    const grouped = {};
    
    diariasFiltradas.forEach(diaria => {
      const clienteId = diaria.cliente_id;
      const clienteNome = getClienteNome(clienteId);
      
      if (!grouped[clienteId]) {
        grouped[clienteId] = {
          id: clienteId,
          nome: clienteNome,
          quantidade: 0,
          faturamento: 0
        };
      }
      
      grouped[clienteId].quantidade += 1;
      grouped[clienteId].faturamento += diaria.valor_cobrar;
    });
    
    return Object.values(grouped).sort((a, b) => b.faturamento - a.faturamento);
  };

  // Calcular top prestadores (filtrados)
  const getTopPrestadores = () => {
    const diariasFiltradas = getDiariasFiltradas();
    const grouped = {};
    
    diariasFiltradas.forEach(diaria => {
      const prestadorId = diaria.prestador_id;
      const prestadorNome = getPrestadorNome(prestadorId);
      
      if (!grouped[prestadorId]) {
        grouped[prestadorId] = {
          id: prestadorId,
          nome: prestadorNome,
          quantidade: 0,
          custoTotal: 0
        };
      }
      
      grouped[prestadorId].quantidade += 1;
      grouped[prestadorId].custoTotal += diaria.valor_pagar;
    });
    
    return Object.values(grouped)
      .sort((a, b) => b.quantidade - a.quantidade)
      .slice(0, 5);
  };

  // Dados para evolução temporal (últimos 7 dias)
  const getEvolucaoTemporal = () => {
    const diariasFiltradas = getDiariasFiltradas();
    const dados = {};
    
    // Últimos 7 dias
    for (let i = 6; i >= 0; i--) {
      const data = new Date();
      data.setDate(data.getDate() - i);
      const dataStr = data.toISOString().split('T')[0];
      
      dados[dataStr] = {
        data: data.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
        diarias: 0,
        faturamento: 0
      };
    }
    
    diariasFiltradas.forEach(diaria => {
      const dataStr = diaria.data;
      if (dados[dataStr]) {
        dados[dataStr].diarias += 1;
        dados[dataStr].faturamento += diaria.valor_cobrar;
      }
    });
    
    return Object.values(dados);
  };

  // Dados para status de pagamento
  const getStatusPagamento = () => {
    const diariasFiltradas = getDiariasFiltradas();
    const pagas = diariasFiltradas.filter(d => d.pago).length;
    const pendentes = diariasFiltradas.filter(d => !d.pago).length;
    
    return [
      { status: 'Pagas', quantidade: pagas, valor: diariasFiltradas.filter(d => d.pago).reduce((sum, d) => sum + d.valor_cobrar, 0) },
      { status: 'Pendentes', quantidade: pendentes, valor: diariasFiltradas.filter(d => !d.pago).reduce((sum, d) => sum + d.valor_cobrar, 0) }
    ];
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Carregando dashboard...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Dashboard</h1>
          <p className="text-gray-600 dark:text-gray-300">Visão geral do sistema de diárias</p>
        </div>
        
        {/* Filtros de Período */}
        <Card className="p-4">
          <div className="flex items-center space-x-4">
            <div>
              <Label htmlFor="periodo">Período</Label>
              <Select value={filtrosPeriodo.tipo} onValueChange={(value) => setFiltrosPeriodo({...filtrosPeriodo, tipo: value})}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="este_mes">Este Mês</SelectItem>
                  <SelectItem value="ultimos_30_dias">Últimos 30 dias</SelectItem>
                  <SelectItem value="trimestre">Este Trimestre</SelectItem>
                  <SelectItem value="ano">Este Ano</SelectItem>
                  <SelectItem value="customizado">Personalizado</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {filtrosPeriodo.tipo === 'customizado' && (
              <>
                <div>
                  <Label htmlFor="data-inicio">Data Início</Label>
                  <DatePicker
                    id="data-inicio"
                    value={filtrosPeriodo.dataInicio}
                    onChange={(value) => setFiltrosPeriodo({...filtrosPeriodo, dataInicio: value})}
                    placeholder="Data início"
                    className="w-40"
                  />
                </div>
                <div>
                  <Label htmlFor="data-fim">Data Fim</Label>
                  <DatePicker
                    id="data-fim"
                    value={filtrosPeriodo.dataFim}
                    onChange={(value) => setFiltrosPeriodo({...filtrosPeriodo, dataFim: value})}
                    placeholder="Data fim"
                    minDate={filtrosPeriodo.dataInicio}
                    className="w-40"
                  />
                </div>
              </>
            )}
          </div>
        </Card>
      </div>

      {/* KPI Cards Aprimorados */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Diárias</CardTitle>
            <CalendarDays className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalDiarias}</div>
            <p className="text-xs text-muted-foreground">
              Diárias no período selecionado
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Faturamento</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(stats.faturamento)}</div>
            <p className={`text-xs ${stats.crescimentoMensal >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
              {formatPercentage(stats.crescimentoMensal)} vs mês anterior
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Valor Médio por Diária</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(stats.valorMedioDiaria)}</div>
            <p className="text-xs text-muted-foreground">
              Receita média por diária
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Diárias Pendentes</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">{stats.diariasPendentes}</div>
            <p className="text-xs text-muted-foreground">
              Aguardando pagamento
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Custos</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600 dark:text-red-400">{formatCurrency(stats.custos)}</div>
            <p className="text-xs text-muted-foreground">
              A pagar no período
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Margem</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">{formatCurrency(stats.margem)}</div>
            <p className="text-xs text-muted-foreground">
              Lucro bruto do período
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Prestadores Ativos</CardTitle>
            <UserCheck className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.prestadoresAtivos}</div>
            <p className="text-xs text-muted-foreground">
              Únicos no período
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Taxa de Ocupação</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {prestadores.length > 0 ? Math.round((stats.prestadoresAtivos / prestadores.length) * 100) : 0}%
            </div>
            <p className="text-xs text-muted-foreground">
              Prestadores em atividade
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Gráficos Aprimorados */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Evolução Temporal */}
        <Card>
          <CardHeader>
            <CardTitle>Evolução Diária</CardTitle>
            <CardDescription>Últimos 7 dias de atividade</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {getEvolucaoTemporal().map((dia) => {
                const maxDiarias = Math.max(...getEvolucaoTemporal().map(d => d.diarias));
                const percentage = maxDiarias > 0 ? (dia.diarias / maxDiarias) * 100 : 0;
                
                return (
                  <div key={dia.data} className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">{dia.data}</span>
                      <div className="text-right">
                        <span className="text-sm font-bold">{dia.diarias} diárias</span>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{formatCurrency(dia.faturamento)}</div>
                      </div>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-muted rounded-full h-2">
                      <div 
                        className="bg-green-600 h-2 rounded-full transition-all duration-500" 
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Status de Pagamento */}
        <Card>
          <CardHeader>
            <CardTitle>Status de Pagamento</CardTitle>
            <CardDescription>Distribuição por status no período</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {getStatusPagamento().map((status) => {
                const total = getStatusPagamento().reduce((sum, s) => sum + s.quantidade, 0);
                const percentage = total > 0 ? (status.quantidade / total) * 100 : 0;
                const cor = status.status === 'Pagas' ? 'bg-green-600' : 'bg-orange-500';
                
                return (
                  <div key={status.status} className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">{status.status}</span>
                      <div className="text-right">
                        <span className="text-sm font-bold">{status.quantidade} diárias</span>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{formatCurrency(status.valor)}</div>
                      </div>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-muted rounded-full h-3">
                      <div 
                        className={`${cor} h-3 rounded-full transition-all duration-500`} 
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 text-right">{percentage.toFixed(1)}%</div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Top Prestadores */}
        <Card>
          <CardHeader>
            <CardTitle>Top 5 Prestadores</CardTitle>
            <CardDescription>Mais ativos no período</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {getTopPrestadores().map((prestador, index) => (
                <div key={prestador.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-muted/40 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-bold">
                        {index + 1}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm font-medium">{prestador.nome}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{formatCurrency(prestador.custoTotal)} total</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold">{prestador.quantidade}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">diárias</div>
                  </div>
                </div>
              ))}
              
              {getTopPrestadores().length === 0 && (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  Nenhum prestador encontrado
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Distribuição por Cliente */}
      <Card>
        <CardHeader>
          <CardTitle>Faturamento por Cliente</CardTitle>
          <CardDescription>Distribuição de receita por cliente no período</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {getDiariasPorCliente().slice(0, 8).map((cliente) => {
              const maxFaturamento = Math.max(...getDiariasPorCliente().map(c => c.faturamento));
              const percentage = maxFaturamento > 0 ? (cliente.faturamento / maxFaturamento) * 100 : 0;
              
              return (
                <div key={cliente.id} className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">{cliente.nome}</span>
                    <div className="text-right">
                      <span className="text-sm font-bold">{formatCurrency(cliente.faturamento)}</span>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{cliente.quantidade} diárias</div>
                    </div>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-muted rounded-full h-3">
                    <div 
                      className="bg-primary h-3 rounded-full transition-all duration-500" 
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                </div>
              );
            })}
            
            {getDiariasPorCliente().length === 0 && (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                Nenhuma diária encontrada
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
